import {useMutation} from '@apollo/client/react';
import {extractGqlData} from '@coolcolduk/ui-apollo-client';
import {useEffect, useState} from 'react';
import {QuestionStatus} from '@cp/model';
import {COUNT_QUESTIONS_BY_FILTERS} from '../sharedQueries';

export interface CountQuestionsVariables {
  moduleId: string;
  topics?: string[];
  difficulties?: number[];
  statuses?: QuestionStatus[];
}

export function useCountQuestions() {
  const [countQuestionsMutation, {loading, error, data}] = useMutation<
    {countQuestionsByFilters: number},
    CountQuestionsVariables
  >(COUNT_QUESTIONS_BY_FILTERS);

  const countQuestions = async (variables: CountQuestionsVariables) => {
    const result = await countQuestionsMutation({
      variables,
    });
    return extractGqlData(result).countQuestionsByFilters || 0;
  };

  return {
    countQuestions,
    loading,
    error,
    count: data?.countQuestionsByFilters || 0,
  };
}

/**
 * Fetch count automatically when parameter updated
 * @param moduleId
 * @param topics
 * @param difficulties
 * @param statuses
 * @returns undefined without module, number of questions otherwise
 */
export function useCountQuestionsFetch(
  moduleId?: string,
  topics?: string[],
  difficulties?: number[],
  statuses?: QuestionStatus[],
) {
  const {countQuestions} = useCountQuestions();
  const [count, setCount] = useState<number | undefined>(undefined);

  // Serialize arrays to avoid reference equality issues
  const topicsKey = topics?.join(',');
  const difficultiesKey = difficulties?.join(',');
  const statusesKey = statuses?.join(',');

  useEffect(() => {
    // Create an abort controller to cancel previous requests
    let isCancelled = false;

    if (moduleId) {
      countQuestions({moduleId, topics, difficulties, statuses})
        .then((result) => {
          // Only update state if the request wasn't cancelled
          if (!isCancelled) {
            setCount(result);
          }
        })
        .catch((error) => {
          // Ignore AbortError - it's expected when requests are cancelled
          if (error.name !== 'AbortError' && !isCancelled) {
            console.error('Error fetching question count:', error);
          }
        });
    } else {
      setCount(undefined);
    }

    // Cleanup function to mark request as cancelled
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, topicsKey, difficultiesKey, statusesKey]);

  return count;
}
